export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function toUtcDateParts(isoString) {
  const [year, month, day] = isoString.split('-').map(Number)
  return { year, month, day }
}

export function utcDateFromIso(isoString) {
  const { year, month, day } = toUtcDateParts(isoString)
  return new Date(Date.UTC(year, month - 1, day))
}

export function dateRangeToIsoDays(startEpoch, endEpoch) {
  if (!startEpoch || !endEpoch) return []

  const start = new Date(startEpoch * 1000)
  const end = new Date(endEpoch * 1000)
  const days = []
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()))
  const endUtc = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()))

  while (cursor <= endUtc) {
    days.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return days
}

export function padIsoDaysToMondayStart(days) {
  if (!days.length) return []

  const first = new Date(days[0])
  const offset = (first.getUTCDay() + 6) % 7
  return [...Array(offset).fill(null), ...days]
}

export function buildCalendarWeeks(days) {
  const base = padIsoDaysToMondayStart(days)
  if (!base.length) return []

  const padded = [...base]
  const remainder = padded.length % 7
  if (remainder) {
    padded.push(...Array(7 - remainder).fill(null))
  }

  const weeks = []
  for (let index = 0; index < padded.length; index += 7) {
    const cells = padded.slice(index, index + 7)
    const actualDates = cells.filter(Boolean)
    weeks.push({
      cells,
      actualDates,
      rangeLabel: buildWeekRangeLabel(actualDates)
    })
  }

  return weeks
}

export function weekLoadingKey(week) {
  if (!week?.actualDates?.length) return null

  const start = Math.floor(new Date(`${week.actualDates[0]}T00:00:00Z`).getTime() / 1000)
  const end = Math.floor(new Date(`${week.actualDates[week.actualDates.length - 1]}T23:59:59Z`).getTime() / 1000)
  return `${start}-${end}`
}

export function buildWeekRangeLabel(dateList) {
  if (!dateList.length) return ''

  const start = formatShortDate(dateList[0])
  const end = formatShortDate(dateList[dateList.length - 1])
  return start === end ? start : `${start} - ${end}`
}

export function formatTooltipDate(isoString) {
  return utcDateFromIso(isoString)
    .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export function formatShortDate(isoString) {
  return utcDateFromIso(isoString)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export function formatBarDay(isoString) {
  return utcDateFromIso(isoString)
    .toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
}

export function formatBarMonthDay(isoString) {
  return utcDateFromIso(isoString)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export function isPastIsoDate(isoString, now = new Date()) {
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const { year, month, day } = toUtcDateParts(isoString)
  const cellUtc = Date.UTC(year, month - 1, day)
  return cellUtc < todayUtc
}

export function countWeekdays(startEpoch, endEpoch) {
  let count = 0
  const cursor = new Date(startEpoch * 1000)
  const end = new Date(endEpoch * 1000)
  cursor.setUTCHours(0, 0, 0, 0)
  end.setUTCHours(0, 0, 0, 0)

  while (cursor <= end) {
    const day = cursor.getUTCDay()
    if (day !== 0 && day !== 6) count += 1
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return count
}

export function countWeekdaysInIsoDates(dates) {
  return dates.reduce((count, date) => {
    if (!date) return count
    const day = new Date(date).getUTCDay()
    return day === 0 || day === 6 ? count : count + 1
  }, 0)
}

export function statsByDate(stats = []) {
  const map = new Map()
  for (const entry of stats) {
    map.set(entry.date, entry)
  }
  return map
}

export function statForDate(dailyStatsByStaff, staffId, date, dailyStatsByStaffDateIndex = null) {
  const indexedStat = dailyStatsByStaffDateIndex?.[staffId]?.[date]
  if (indexedStat) return indexedStat

  return dailyStatsByStaff?.[staffId]?.find(stat => stat.date === date) || {}
}

export function statHours(stat = {}) {
  const billable = stat.billableHrs || 0
  const clientNonBillable = stat.clientNonBillableHrs || 0
  const internalNonBillable = stat.internalNonBillableHrs || 0
  const pto = stat.ptoHrs || 0
  const total = stat.totalHrs || 0
  const accounted = billable + clientNonBillable + internalNonBillable + pto

  return {
    billable,
    clientNonBillable,
    internalNonBillable,
    pto,
    total,
    unaccounted: Math.max(0, total - accounted)
  }
}

export function sumStats(stats = []) {
  return stats.reduce((totals, stat) => {
    const hours = statHours(stat)
    totals.billable += hours.billable
    totals.clientNonBillable += hours.clientNonBillable
    totals.internalNonBillable += hours.internalNonBillable
    totals.pto += hours.pto
    totals.total += hours.total
    return totals
  }, {
    billable: 0,
    clientNonBillable: 0,
    internalNonBillable: 0,
    pto: 0,
    total: 0
  })
}

export function staffRangeTotals(dailyStatsByStaff, staffId) {
  const totals = sumStats(dailyStatsByStaff?.[staffId] || [])
  return {
    ...totals,
    workedTotal: totals.billable + totals.clientNonBillable + totals.internalNonBillable
  }
}

export function targetHoursForWeekdays(weekdays, weeklyTarget) {
  const hoursPerWeekday = weeklyTarget > 0 ? weeklyTarget / 5 : 0
  return Math.max(0, +(weekdays * hoursPerWeekday).toFixed(2))
}

export function computeGaugeTotals(stats, startEpoch, endEpoch, weeklyTarget) {
  if (!stats?.length || !startEpoch || !endEpoch) {
    return emptyGaugeTotals()
  }

  const totals = sumStatsForGauge(stats)
  const denom = targetHoursForWeekdays(countWeekdays(startEpoch, endEpoch), weeklyTarget)
  return withUnaccounted(totals, denom)
}

export function computeWeeklyTotals(stats, dates, weeklyTarget) {
  const byDate = statsByDate(stats)
  const totals = { billable: 0, clientNonBillable: 0, internalNonBillable: 0, pto: 0 }

  for (const date of dates) {
    if (!date) continue
    const record = byDate.get(date)
    if (!record) continue

    const recordTotals = statTotalsForGauge(record)
    totals.billable += recordTotals.billable
    totals.clientNonBillable += recordTotals.clientNonBillable
    totals.internalNonBillable += recordTotals.internalNonBillable
    totals.pto += recordTotals.pto
  }

  const denom = targetHoursForWeekdays(countWeekdaysInIsoDates(dates), weeklyTarget)
  return withUnaccounted(totals, denom)
}

export function formatHoursToParts(value) {
  const hoursValue = typeof value === 'number' ? value : value?.value || 0
  const totalMinutes = Math.max(0, Math.round(hoursValue * 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0')
  }
}

export function interpolateColor(startHex, endHex, ratio) {
  const toRgbInt = hex => parseInt(hex.slice(1), 16)
  const start = toRgbInt(startHex)
  const end = toRgbInt(endHex)
  const r1 = start >> 16
  const g1 = (start >> 8) & 0xFF
  const b1 = start & 0xFF
  const r2 = end >> 16
  const g2 = (end >> 8) & 0xFF
  const b2 = end & 0xFF
  const r = Math.round(r1 + (r2 - r1) * ratio)
  const g = Math.round(g1 + (g2 - g1) * ratio)
  const b = Math.round(b1 + (b2 - b1) * ratio)

  return `rgb(${r},${g},${b})`
}

export function heatmapCellBackground(billablePct) {
  return billablePct <= 50
    ? interpolateColor('#ef4444', '#0DABCB', billablePct / 50)
    : interpolateColor('#0DABCB', '#83BD48', (billablePct - 50) / 50)
}

export function isWeekendIsoDate(isoString) {
  const day = utcDateFromIso(isoString).getUTCDay()
  return day === 0 || day === 6
}

export function heatmapWorkedCellBackground(stat = {}, includeClientAsBillable = false) {
  const hours = statHours(stat)
  const total = hours.billable + hours.clientNonBillable + hours.internalNonBillable + hours.pto
  if (total <= 0) return '#f8fafc'

  const effectiveBillable = includeClientAsBillable
    ? hours.billable + hours.clientNonBillable
    : hours.billable
  const billableRatio = effectiveBillable / total
  const clientRatio = hours.clientNonBillable / total
  const internalRatio = hours.internalNonBillable / total
  const ptoRatio = hours.pto / total
  const dominant = includeClientAsBillable
    ? Math.max(billableRatio, internalRatio, ptoRatio)
    : Math.max(billableRatio, clientRatio, internalRatio, ptoRatio)

  if (dominant === ptoRatio) return '#C4B5FD'
  if (!includeClientAsBillable && dominant === clientRatio) return '#7FD2E2'
  if (dominant === internalRatio) return '#FFD362'
  return billableRatio >= 0.75 ? '#98D87B' : '#C6E9B6'
}

function emptyGaugeTotals() {
  return {
    billable: 0,
    clientNonBillable: 0,
    internalNonBillable: 0,
    pto: 0,
    unaccounted: 0,
    denom: 0
  }
}

function sumStatsForGauge(stats) {
  return stats.reduce((totals, stat) => {
    const recordTotals = statTotalsForGauge(stat)
    totals.billable += recordTotals.billable
    totals.clientNonBillable += recordTotals.clientNonBillable
    totals.internalNonBillable += recordTotals.internalNonBillable
    totals.pto += recordTotals.pto
    return totals
  }, {
    billable: 0,
    clientNonBillable: 0,
    internalNonBillable: 0,
    pto: 0
  })
}

function statTotalsForGauge(stat) {
  const billable = stat.billableHrs || 0
  const pto = stat.ptoHrs || 0
  let clientNonBillable = 0
  let internalNonBillable = 0

  if (typeof stat.clientNonBillableHrs === 'number') {
    clientNonBillable += stat.clientNonBillableHrs || 0
  }
  if (typeof stat.internalNonBillableHrs === 'number') {
    internalNonBillable += stat.internalNonBillableHrs || 0
  }
  if (typeof stat.clientNonBillableHrs !== 'number' && typeof stat.internalNonBillableHrs !== 'number') {
    clientNonBillable += Math.max(0, (stat.totalHrs || 0) - billable)
  }

  return {
    billable,
    clientNonBillable,
    internalNonBillable: internalNonBillable + pto,
    pto
  }
}

function withUnaccounted(totals, denom) {
  const accounted = totals.billable + totals.clientNonBillable + totals.internalNonBillable
  return {
    ...totals,
    unaccounted: Math.max(0, +(denom - accounted).toFixed(2)),
    denom
  }
}
